import React, { useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { SpotlightTarget } from '@atlaskit/onboarding';

import { LoadingStatus } from 'src/constants/loading-status';
import {
  fetchJiraProjectsForSite,
  onCreateJiraIssueFormChangeVisibility,
  setPrFormErrorState,
} from 'src/redux/jira/actions';
import { IssueCreationFailureReason } from 'src/redux/jira/constants';
import { Project, Site } from 'src/redux/jira/types';
import { getCurrentPullRequestId } from 'src/redux/pull-request/selectors';
import { BucketState } from 'src/types/state';

import { CreateJiraIssueInput } from './create-jira-issue-input';
import {
  CreateJiraIssueOnboarding,
  TARGETS,
} from './create-jira-issue-onboarding';
import * as styles from './create-jira-issue-pr.styled';
import { renderIssueMetaError } from './create-jira-issue-render-error';
import messages from './create-jira-issue.i18n';
import { ProjectChooser } from './project-chooser';
import { SiteChooser } from './site-chooser';
import { useIssueCreation } from './use-issue-creation';
import { useIssueMeta } from './use-issue-meta';
import { useIssueSummary } from './use-issue-summary';

type Props = {
  commentId: number;
};

const getDefaultSite = (sites: Site[], cloudId: string) => {
  const defaultSite = sites.find(site => site.cloudId === cloudId) || sites[0];
  return defaultSite;
};

export const CreateJiraIssue = React.memo(({ commentId }: Props) => {
  const dispatch = useDispatch();
  const { issueSummary, handleIssueSummaryChange } = useIssueSummary();
  const [selectedSite, setSelectedSite] = useState<Site>();
  const [selectedProject, setSelectedProject] = useState<Project | undefined>();
  const currentPullRequestId = useSelector<
    BucketState,
    number | null | undefined
  >(getCurrentPullRequestId);

  const { issueMeta, selectedIssueType, handleSetSelectedIssueType } =
    useIssueMeta(selectedProject, commentId, selectedSite);
  const {
    issueCreationFailureReason,
    createPullRequestJiraIssue,
    isCreatingIssue,
    isInvalid,
  } = useIssueCreation(
    selectedProject,
    selectedIssueType,
    issueSummary,
    commentId,
    currentPullRequestId
  );

  const preferences = useSelector<
    BucketState,
    { cloudId: string; projectId: string } | undefined
  >(state => state.jira.createJiraIssue.preferences.value);

  const sites = useSelector<
    BucketState,
    { fetchedStatus: LoadingStatus; list: Site[] }
  >(state => ({
    fetchedStatus: state.jira.createJiraIssue.connectedSitesFetchedStatus,
    list: state.jira.createJiraIssue.connectedSites,
  }));

  const projects = useSelector<
    BucketState,
    { list: Project[]; fetchedStatus: LoadingStatus }
  >(state => {
    if (
      selectedSite &&
      state.jira.createJiraIssue.projects[commentId] &&
      state.jira.createJiraIssue.projects[commentId][selectedSite.cloudId]
    ) {
      return state.jira.createJiraIssue.projects[commentId][
        selectedSite.cloudId
      ];
    }
    return {
      list: [],
      fetchedStatus: LoadingStatus.Before,
    };
  });

  const defaultProject = useSelector<
    BucketState,
    { project?: Project; fetchedStatus: LoadingStatus }
  >(state => {
    if (selectedSite) {
      const projectForSite =
        state.jira.createJiraIssue.defaultProject[selectedSite.cloudId];
      if (projectForSite) {
        return projectForSite;
      }
    }
    return {
      project: undefined,
      fetchedStatus: LoadingStatus.Before,
    };
  });

  useEffect(() => {
    if (
      !selectedSite &&
      sites.fetchedStatus === LoadingStatus.Success &&
      sites.list.length > 0
    ) {
      setSelectedSite(
        getDefaultSite(sites.list, preferences ? preferences.cloudId : '')
      );
    }
  }, [selectedSite, sites, preferences]);

  useEffect(() => {
    if (selectedSite && projects.fetchedStatus === LoadingStatus.Before) {
      dispatch(
        fetchJiraProjectsForSite({
          site: selectedSite,
          projectFilter: '',
          commentId,
        })
      );
    }
  }, [selectedSite, projects.fetchedStatus, dispatch, commentId]);

  // The below hook is for setting a default project if there is nothing selected already.
  useEffect(() => {
    if (!selectedSite) {
      return;
    }

    // When there are no project selected we need to choose a default one:
    // - If there is a default project, and the cloud id of it matches
    //   the selected site cloud id choose that one.
    // - Otherwise choose first one from the list of projects as initial selected project.
    if (!selectedProject) {
      if (
        defaultProject.project &&
        defaultProject.fetchedStatus === LoadingStatus.Success &&
        selectedSite.cloudId === defaultProject.project?.site?.cloudId
      ) {
        setSelectedProject(defaultProject.project);
      } else if (
        projects.fetchedStatus === LoadingStatus.Success &&
        projects.list.length > 0
      ) {
        setSelectedProject(projects.list[0]);
      }
    }
  }, [
    selectedSite,
    selectedProject,
    projects,
    defaultProject.project,
    defaultProject.fetchedStatus,
  ]);

  const isProjectChooserDisabled =
    isCreatingIssue ||
    projects.fetchedStatus === LoadingStatus.Forbidden ||
    projects.fetchedStatus === LoadingStatus.Failed;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isInvalid) {
      return;
    }
    createPullRequestJiraIssue();
  };

  useEffect(() => {
    dispatch(
      // Reset the error state when selectedSite changes.
      setPrFormErrorState({
        commentId,
        status: LoadingStatus.Before,
        failureReason: IssueCreationFailureReason.None,
      })
    );
  }, [selectedSite, commentId, dispatch]);

  const handleSelectedSiteChange = (site: Site) => {
    setSelectedProject(undefined);
    setSelectedSite(site);
  };

  return (
    <React.Fragment>
      <styles.CreateJiraIssueWrapper
        onSubmit={handleSubmit}
        data-testid="pr-comment-create-jira-issue-form"
      >
        <SpotlightTarget name={TARGETS.ISSUE_SUMMARY}>
          <CreateJiraIssueInput
            issueSummary={issueSummary}
            handleIssueSummaryChange={handleIssueSummaryChange}
            selectedIssueType={selectedIssueType}
            handleSetSelectedIssueType={handleSetSelectedIssueType}
            isCreatingIssue={isCreatingIssue}
            isInvalid={isInvalid}
            issueMeta={issueMeta}
          />
        </SpotlightTarget>
        {renderIssueMetaError(
          issueMeta.fetchedStatus,
          issueCreationFailureReason,
          selectedProject,
          selectedIssueType
        )}
        <styles.CreateJiraIssueActionsContainer>
          <SpotlightTarget name={TARGETS.ISSUE_ACTIONS}>
            <styles.CreateJiraIssueActionsInnerContainer>
              <FormattedMessage {...messages.siteChooserLabel} />
              <SiteChooser
                fetchedStatus={sites.fetchedStatus}
                isDisabled={isCreatingIssue}
                onSelectedChange={handleSelectedSiteChange}
                selected={selectedSite}
                sites={sites.list}
              />
              <FormattedMessage {...messages.projectChooserLabel} />
              <ProjectChooser
                fetchedStatus={projects.fetchedStatus}
                isDisabled={isProjectChooserDisabled}
                onSelectedChange={setSelectedProject}
                projects={projects.list || []}
                selected={selectedProject}
                fetchJiraProjectsForSite={fetchJiraProjectsForSite}
                site={selectedSite!}
                commentId={commentId}
              />
            </styles.CreateJiraIssueActionsInnerContainer>
          </SpotlightTarget>
          <div>
            <styles.CancelButton
              appearance="subtle-link"
              isDisabled={isCreatingIssue}
              onClick={() =>
                dispatch(
                  onCreateJiraIssueFormChangeVisibility({
                    commentId,
                    isVisible: false,
                  })
                )
              }
            >
              <FormattedMessage {...messages.cancelButtonText} />
            </styles.CancelButton>
            <styles.SubmitButton
              appearance="subtle-link"
              isDisabled={isCreatingIssue}
              onClick={handleSubmit}
            >
              <FormattedMessage {...messages.submitButtonText} />
            </styles.SubmitButton>
          </div>
        </styles.CreateJiraIssueActionsContainer>
      </styles.CreateJiraIssueWrapper>
      <CreateJiraIssueOnboarding />
    </React.Fragment>
  );
});
